import { create } from 'zustand';
import { api, type Subscription, type SubscriptionUsage } from '../lib/api';

export type PlanType = 'free' | 'starter' | 'professional' | 'unlimited';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired';

interface PlanLimits {
  projects: number;
  wordsPerMonth: number;
  collaborators: number; // -1 = unlimited
  features: {
    exportPdf: boolean;
    exportEpub: boolean;
    exportDocx: boolean;
    versionHistory: boolean;
    cloudBackup: boolean;
    collaboration: boolean;
    aiAssistant: boolean;
    aiGrammar: boolean;
    advancedAnalytics: boolean;
    exclusiveTemplates: boolean;
    kdpPublishing: boolean;
    aiCoverGenerator: boolean;
    apiAccess: boolean;
    messages: boolean;
    professionals: boolean;
    canHire: boolean;
  };
}

// Plan configuration
export const PLAN_LIMITS: Record<PlanType, PlanLimits> = {
  free: {
    projects: 1,
    wordsPerMonth: 10000,
    collaborators: 0,
    features: {
      exportPdf: true,
      exportEpub: false,
      exportDocx: false,
      versionHistory: false,
      cloudBackup: false,
      collaboration: false,
      aiAssistant: false,
      aiGrammar: false,
      advancedAnalytics: false,
      exclusiveTemplates: false,
      kdpPublishing: false,
      aiCoverGenerator: false,
      apiAccess: false,
      messages: true,
      professionals: true,
      canHire: false,
    },
  },
  starter: {
    projects: 5,
    wordsPerMonth: 50000,
    collaborators: 0,
    features: {
      exportPdf: true,
      exportEpub: true,
      exportDocx: true,
      versionHistory: false,
      cloudBackup: true,
      collaboration: false,
      aiAssistant: false,
      aiGrammar: false,
      advancedAnalytics: false,
      exclusiveTemplates: false,
      kdpPublishing: false,
      aiCoverGenerator: false,
      apiAccess: false,
      messages: true,
      professionals: true,
      canHire: true,
    },
  },
  professional: {
    projects: 20,
    wordsPerMonth: -1, // unlimited
    collaborators: 0,
    features: {
      exportPdf: true,
      exportEpub: true,
      exportDocx: true,
      // Version history is part of the Craft add-on — enabled via add-on model
      versionHistory: true,
      cloudBackup: true,
      collaboration: false,
      aiAssistant: true,
      aiGrammar: true,
      advancedAnalytics: true,
      exclusiveTemplates: true,
      kdpPublishing: false,
      aiCoverGenerator: false,
      apiAccess: false,
      messages: true,
      professionals: true,
      canHire: true,
    },
  },
  unlimited: {
    projects: -1, // unlimited
    wordsPerMonth: -1,
    collaborators: 0,
    features: {
      exportPdf: true,
      exportEpub: true,
      exportDocx: true,
      versionHistory: true,
      cloudBackup: true,
      collaboration: false,
      aiAssistant: true,
      aiGrammar: true,
      advancedAnalytics: true,
      exclusiveTemplates: true,
      kdpPublishing: true,
      aiCoverGenerator: true,
      apiAccess: true,
      messages: true,
      professionals: true,
      canHire: true,
    },
  },
};

export const PLAN_NAMES: Record<PlanType, string> = {
  free: 'Free',
  starter: 'Starter',
  professional: 'Professional',
  unlimited: 'Unlimited',
};

export const PLAN_PRICES: Record<PlanType, number> = {
  free: 0,
  starter: 19.90,
  professional: 39.90,
  unlimited: 79.90,
};

// AI requests per month per plan
// Note: IA Studio add-on grants ≈1.500 actions/month (fair-use cap).
// These limits reflect the legacy plan model; new subscriptions use the add-on model.
export const AI_LIMITS: Record<PlanType, number> = {
  free: 25,
  starter: 500,
  professional: 1500,
  unlimited: 1500, // capped at ≈1.500 actions/month (IA Studio fair-use)
};

interface SubscriptionState {
  // State
  subscription: Subscription | null;
  usage: SubscriptionUsage | null;
  isLoading: boolean;
  error: string | null;

  // Computed
  plan: PlanType;
  status: SubscriptionStatus;
  isTrialing: boolean;
  trialDaysRemaining: number;
  daysRemaining: number; // Alias for trialDaysRemaining
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  planLimits: PlanLimits;

  // Actions
  fetchSubscription: () => Promise<void>;
  fetchUsage: () => Promise<void>;
  refresh: () => Promise<void>;

  // Validation helpers
  canCreateProject: () => boolean;
  canWriteWords: (count: number) => boolean;
  canUseFeature: (feature: keyof PlanLimits['features']) => boolean;
  getProjectsRemaining: () => number;
  getWordsRemaining: () => number;
  getAILimit: () => number;
  canUseAI: () => boolean;
}

export const useSubscriptionStore = create<SubscriptionState>()((set, get) => ({
  // Initial state
  subscription: null,
  usage: null,
  isLoading: false,
  error: null,

  // Computed getters
  get plan() {
    return (get().subscription?.plan || 'free') as PlanType;
  },

  get status() {
    return (get().subscription?.status || 'active') as SubscriptionStatus;
  },

  get isTrialing() {
    return get().subscription?.status === 'trialing';
  },

  get trialDaysRemaining() {
    const sub = get().subscription;
    if (!sub?.trialEndsAt) return 0;
    const trialEnd = new Date(sub.trialEndsAt);
    const now = new Date();
    const diff = trialEnd.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  },

  get daysRemaining() {
    // Alias for trialDaysRemaining
    const sub = get().subscription;
    if (!sub?.trialEndsAt) return 0;
    const trialEnd = new Date(sub.trialEndsAt);
    const now = new Date();
    const diff = trialEnd.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  },

  get trialEndsAt() {
    return get().subscription?.trialEndsAt || null;
  },

  get currentPeriodEnd() {
    return get().subscription?.currentPeriodEnd || null;
  },

  get cancelAtPeriodEnd() {
    return get().subscription?.cancelAtPeriodEnd || false;
  },

  get planLimits() {
    const plan = get().plan;
    return PLAN_LIMITS[plan];
  },

  // Actions
  fetchSubscription: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.getCurrentSubscription();
      set({ subscription: response.subscription, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch subscription';
      set({ error: message, isLoading: false });
    }
  },

  fetchUsage: async () => {
    try {
      const response = await api.getSubscriptionUsage();
      set({ usage: response.usage });
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  },

  refresh: async () => {
    await Promise.all([get().fetchSubscription(), get().fetchUsage()]);
  },

  // Validation helpers
  canCreateProject: () => {
    const { usage, planLimits } = get();
    if (!usage) return true; // Allow if we haven't fetched usage yet
    if (planLimits.projects === -1) return true; // Unlimited
    return usage.projects.used < usage.projects.limit;
  },

  canWriteWords: (count: number) => {
    const { usage, planLimits } = get();
    if (!usage) return true;
    if (planLimits.wordsPerMonth === -1) return true; // Unlimited
    const remaining = (usage.words.limit || 0) - (usage.words.used || 0);
    return remaining >= count;
  },

  canUseFeature: (feature: keyof PlanLimits['features']) => {
    const { planLimits } = get();
    return planLimits.features[feature];
  },

  getProjectsRemaining: () => {
    const { usage, planLimits } = get();
    if (!usage) return 0;
    if (planLimits.projects === -1) return Infinity;
    return Math.max(0, usage.projects.limit - usage.projects.used);
  },

  getWordsRemaining: () => {
    const { usage, planLimits } = get();
    if (!usage) return 0;
    if (planLimits.wordsPerMonth === -1) return Infinity;
    return Math.max(0, (usage.words.limit || 0) - (usage.words.used || 0));
  },

  getAILimit: () => {
    const plan = get().plan;
    return AI_LIMITS[plan];
  },

  canUseAI: () => {
    const { planLimits } = get();
    return planLimits.features.aiAssistant || planLimits.features.aiGrammar;
  },
}));
